import { useCallback, useState } from 'react'
import type { FormEvent } from 'react'
import type { CreateProCrew } from '@/modules/auth/use-cases'
import { ProRequiredError } from '@/shared/domain'
import { reportError } from '@/shared/infra/observability'
import { useSubmitGuard } from '@/shared/ui/hooks'
import { titleCase } from './title-case'

export const CREW_NAME_MIN_LENGTH = 2
export const CREW_NAME_MAX_LENGTH = 255
export const CREW_NAME_TOO_SHORT_MESSAGE = 'Enter a crew name with at least 2 characters.'
export const CREW_NAME_TOO_LONG_MESSAGE = 'Crew names can be up to 255 characters.'
export const CREW_CREATE_FAILED_MESSAGE = "We couldn't create your crew. Please try again."

export type UseCrewNameStepProps = {
  onCreateProCrew: CreateProCrew
  onProRequired: () => void
  onCreated?: () => void
}

export type UseCrewNameStepResult = {
  name: string
  error: string
  isSubmitting: boolean
  isCreated: boolean
  createdOrgId: string | null
  setName: (value: string) => void
  handleSubmit: (event?: FormEvent) => Promise<void>
  finish: () => void
}

function validateName(trimmed: string): string {
  if (trimmed.length < CREW_NAME_MIN_LENGTH) return CREW_NAME_TOO_SHORT_MESSAGE
  if (trimmed.length > CREW_NAME_MAX_LENGTH) return CREW_NAME_TOO_LONG_MESSAGE
  return ''
}

export function useCrewNameStep({
  onCreateProCrew,
  onProRequired,
  onCreated,
}: UseCrewNameStepProps): UseCrewNameStepResult {
  const { isSubmitting, runGuarded } = useSubmitGuard()
  const [name, setNameState] = useState('')
  const [error, setError] = useState('')
  const [createdOrgId, setCreatedOrgId] = useState<string | null>(null)
  const isCreated = createdOrgId !== null

  const setName = useCallback((value: string) => {
    setNameState(titleCase(value))
    setError('')
  }, [])

  const handleSubmit = useCallback(
    async (event?: FormEvent) => {
      event?.preventDefault()
      await runGuarded(async () => {
        if (isCreated) return
        const trimmed = name.trim()
        const validation = validateName(trimmed)
        if (validation) {
          setError(validation)
          return
        }
        setError('')
        try {
          const created = await onCreateProCrew({ name: trimmed })
          setCreatedOrgId(created.orgId)
          onCreated?.()
          window.flutter_inappwebview
            ?.callHandler?.('crewCreated', { orgId: created.orgId, orgType: 'crew' })
            ?.catch(() => undefined)
        } catch (err) {
          if (err instanceof ProRequiredError) {
            onProRequired()
            return
          }
          const message = reportError(err, { fallbackMessage: CREW_CREATE_FAILED_MESSAGE })
          setError(message)
          window.flutter_inappwebview?.callHandler?.('orgError', { message })
        }
      })
    },
    [runGuarded, isCreated, name, onCreateProCrew, onProRequired, onCreated],
  )

  const finish = useCallback(() => {
    if (!createdOrgId) return
    window.flutter_inappwebview?.callHandler?.('orgCreated', {
      orgId: createdOrgId,
      orgType: 'crew',
    })
  }, [createdOrgId])

  return { name, error, isSubmitting, isCreated, createdOrgId, setName, handleSubmit, finish }
}
